import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Image, Download, Loader2, RefreshCw, Sparkles, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STYLES = [
  { value: 'modern', label: 'Modern' },
  { value: 'classic', label: 'Classic' },
  { value: 'bold', label: 'Bold' },
  { value: 'minimal', label: 'Minimal' },
];

const COLORS = [
  { value: 'navy-gold', label: 'Navy & Gold' },
  { value: 'warm', label: 'Warm Tones' },
  { value: 'cool', label: 'Cool Tones' },
  { value: 'earth', label: 'Earthy' },
  { value: 'vibrant', label: 'Vibrant' },
];

const TYPES = [
  { value: 'event', label: 'Event' },
  { value: 'bible-study', label: 'Bible Study' },
  { value: 'service', label: 'Worship Service' },
  { value: 'announcement', label: 'Announcement' },
  { value: 'youth', label: 'Youth Ministry' },
];

export default function FlyerGenerator({ onClose, user }) {
  const [form, setForm] = useState({
    title: '', subtitle: '', event_date: '', event_time: '',
    location: '', church_name: user?.church_name || '',
    style: 'modern', color_scheme: 'navy-gold', flyer_type: 'event',
  });
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [pastFlyers, setPastFlyers] = useState([]);
  const [previewFlyer, setPreviewFlyer] = useState(null);

  useEffect(() => { loadPastFlyers(); }, []);

  const loadPastFlyers = async () => {
    try {
      const res = await axios.get(`${API}/flyers`);
      setPastFlyers(res.data.flyers || []);
    } catch (e) {}
  };

  const handleGenerate = async () => {
    if (!form.title) return;
    setGenerating(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/generate/flyer`, form);
      const jobId = res.data.job_id;

      for (let i = 0; i < 40; i++) {
        await new Promise(r => setTimeout(r, 3000));
        const status = await axios.get(`${API}/generate/flyer/${jobId}`);
        if (status.data.status === 'completed') {
          setResult(status.data);
          loadPastFlyers();
          break;
        } else if (status.data.status === 'failed') {
          alert(status.data.error || 'Flyer generation failed');
          break;
        }
      }
    } catch (e) {
      alert('Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = (base64Data, title) => {
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${base64Data}`;
    link.download = `${title.replace(/\s+/g, '-').toLowerCase()}-flyer.png`;
    link.click();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto p-4 pt-8" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-[#F9F8F6] rounded-2xl w-full max-w-5xl shadow-2xl" data-testid="flyer-generator">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E2DFD8] bg-white rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#1B3A6B] flex items-center justify-center">
              <Image className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-bold text-[#1B3A6B]">Flyer Generator</h2>
              <p className="text-xs text-[#94A3B8]">AI-powered church flyers & event graphics</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#F4F2EC] rounded-lg"><X className="w-5 h-5 text-[#475569]" /></button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
          {/* Left: Form */}
          <div className="p-6 border-r border-[#E2DFD8]">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-[#0F1A2A] block mb-1">Flyer Title *</label>
                <input value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))} placeholder="e.g. Easter Sunday Celebration" className="w-full px-3 py-2.5 border border-[#E2DFD8] rounded-lg text-sm bg-white" data-testid="flyer-title" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#0F1A2A] block mb-1">Subtitle</label>
                <input value={form.subtitle} onChange={e => setForm(p => ({...p, subtitle: e.target.value}))} placeholder="e.g. Join us for a special worship experience" className="w-full px-3 py-2.5 border border-[#E2DFD8] rounded-lg text-sm bg-white" data-testid="flyer-subtitle" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-[#0F1A2A] block mb-1">Date</label>
                  <input value={form.event_date} onChange={e => setForm(p => ({...p, event_date: e.target.value}))} placeholder="e.g. April 20, 2026" className="w-full px-3 py-2.5 border border-[#E2DFD8] rounded-lg text-sm bg-white" data-testid="flyer-date" />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#0F1A2A] block mb-1">Time</label>
                  <input value={form.event_time} onChange={e => setForm(p => ({...p, event_time: e.target.value}))} placeholder="e.g. 10:00 AM" className="w-full px-3 py-2.5 border border-[#E2DFD8] rounded-lg text-sm bg-white" data-testid="flyer-time" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-[#0F1A2A] block mb-1">Location</label>
                  <input value={form.location} onChange={e => setForm(p => ({...p, location: e.target.value}))} placeholder="e.g. Main Sanctuary" className="w-full px-3 py-2.5 border border-[#E2DFD8] rounded-lg text-sm bg-white" data-testid="flyer-location" />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#0F1A2A] block mb-1">Church Name</label>
                  <input value={form.church_name} onChange={e => setForm(p => ({...p, church_name: e.target.value}))} className="w-full px-3 py-2.5 border border-[#E2DFD8] rounded-lg text-sm bg-white" data-testid="flyer-church" />
                </div>
              </div>

              {/* Style Options */}
              <div>
                <label className="text-sm font-medium text-[#0F1A2A] block mb-2">Flyer Type</label>
                <div className="flex flex-wrap gap-2">
                  {TYPES.map(t => (
                    <button key={t.value} onClick={() => setForm(p => ({...p, flyer_type: t.value}))} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${form.flyer_type === t.value ? 'bg-[#1B3A6B] text-white' : 'bg-white border border-[#E2DFD8] text-[#475569] hover:bg-[#F4F2EC]'}`} data-testid={`type-${t.value}`}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-[#0F1A2A] block mb-2">Design Style</label>
                <div className="flex flex-wrap gap-2">
                  {STYLES.map(s => (
                    <button key={s.value} onClick={() => setForm(p => ({...p, style: s.value}))} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${form.style === s.value ? 'bg-[#B8860B] text-white' : 'bg-white border border-[#E2DFD8] text-[#475569] hover:bg-[#F4F2EC]'}`} data-testid={`style-${s.value}`}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-[#0F1A2A] block mb-2">Color Scheme</label>
                <div className="flex flex-wrap gap-2">
                  {COLORS.map(c => (
                    <button key={c.value} onClick={() => setForm(p => ({...p, color_scheme: c.value}))} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${form.color_scheme === c.value ? 'bg-[#1B3A6B] text-white' : 'bg-white border border-[#E2DFD8] text-[#475569] hover:bg-[#F4F2EC]'}`} data-testid={`color-${c.value}`}>
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={handleGenerate} disabled={generating || !form.title} className="w-full py-3 bg-[#1B3A6B] text-white rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-[#254B8A] transition-colors" data-testid="flyer-generate-btn">
                {generating ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Generating flyer...</>
                ) : (
                  <><Sparkles className="w-4 h-4" /> Generate Flyer</>
                )}
              </button>
            </div>
          </div>

          {/* Right: Preview + Past Flyers */}
          <div className="p-6">
            {/* Current result */}
            {result?.image_base64 ? (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-[#0F1A2A]">Your Flyer</h3>
                  <div className="flex gap-2">
                    <button onClick={() => handleDownload(result.image_base64, form.title)} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1B3A6B] text-white rounded-lg text-sm font-medium hover:bg-[#254B8A]" data-testid="flyer-download">
                      <Download className="w-3 h-3" /> Download
                    </button>
                    <button onClick={handleGenerate} className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[#E2DFD8] text-[#475569] rounded-lg text-sm hover:bg-[#F4F2EC]">
                      <RefreshCw className="w-3 h-3" /> Regenerate
                    </button>
                  </div>
                </div>
                <img src={`data:image/png;base64,${result.image_base64}`} alt={form.title} className="w-full rounded-xl shadow-lg" data-testid="flyer-result" />
              </div>
            ) : generating ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <Loader2 className="w-10 h-10 animate-spin text-[#1B3A6B] mb-4" />
                <p className="text-[#475569] font-medium">Creating your flyer...</p>
                <p className="text-xs text-[#94A3B8] mt-1">This takes about 30 seconds</p>
              </div>
            ) : (
              <div>
                <div className="flex flex-col items-center justify-center py-12 text-center mb-6 bg-white rounded-xl border-2 border-dashed border-[#E2DFD8]">
                  <Image className="w-12 h-12 text-[#E2DFD8] mb-3" />
                  <p className="text-[#475569] font-medium">Your flyer will appear here</p>
                  <p className="text-xs text-[#94A3B8] mt-1">Fill in the details and click Generate</p>
                </div>

                {/* Past Flyers */}
                {pastFlyers.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-[#0F1A2A] mb-3">Recent Flyers</h3>
                    <div className="grid grid-cols-2 gap-3">
                      {pastFlyers.map(f => (
                        <button key={f.job_id} onClick={async () => {
                          try {
                            const res = await axios.get(`${API}/generate/flyer/${f.job_id}`);
                            if (res.data.image_base64) setResult(res.data);
                          } catch(e) {}
                        }} className="text-left bg-white rounded-lg p-3 border border-[#E2DFD8] hover:shadow-md transition-shadow">
                          {f.image_url && <img src={f.image_url} alt={f.title} className="w-full h-24 object-cover rounded mb-2" />}
                          <p className="text-xs font-medium text-[#0F1A2A] truncate">{f.title}</p>
                          <p className="text-[10px] text-[#94A3B8]">{new Date(f.created_at).toLocaleDateString()}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Full Preview Modal */}
      {previewFlyer && (
        <div className="fixed inset-0 bg-black/80 z-60 flex items-center justify-center p-4" onClick={() => setPreviewFlyer(null)}>
          <img src={previewFlyer} alt="Flyer preview" className="max-h-[90vh] max-w-[90vw] rounded-xl shadow-2xl" />
        </div>
      )}
    </div>
  );
}
